/**
 * First-touch acquisition attribution for PhotoClear sign-ups.
 * Behaviour is owned by @rehub/tracking so the storage key and parameter
 * list stay compatible with the existing PhotoClear tests.
 */

import {
  LEGACY_ATTRIBUTION_PARAMS,
  PAID_MEDIUMS,
  acquisitionSourceLabel,
  isGoogleAdsAttribution,
  isPaidMedium,
  legacyAttribution,
  type AttributionPayload,
  type AttributionStore,
  type FirstTouchAttribution,
} from '@rehub/tracking';

export {
  PAID_MEDIUMS,
  acquisitionSourceLabel,
  isGoogleAdsAttribution,
  isPaidMedium,
};
export type { AttributionPayload, AttributionStore, FirstTouchAttribution };

export const ATTRIBUTION_STORAGE_KEY = 'photoclear_acquisition_first';
export const ATTRIBUTION_PARAMS = LEGACY_ATTRIBUTION_PARAMS;

export const readFirstTouch = legacyAttribution.readFirstTouch;
export const captureFirstTouch = legacyAttribution.captureFirstTouch;
export const authCallbackUrl = legacyAttribution.authCallbackUrl;
export const attributionPayload = legacyAttribution.attributionPayload;
export const signupUserData = legacyAttribution.signupUserData;

const memoryStore: AttributionStore = (() => {
  const data = new Map<string, string>();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, value);
    },
  };
})();

function browserStore(): AttributionStore {
  if (typeof window === 'undefined') return memoryStore;
  try {
    const probe = '__pc_attr__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return memoryStore;
  }
}

/** Call once on boot, before the address bar is cleaned. */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  captureFirstTouch({
    pathname: window.location.pathname,
    search: window.location.search,
    storage: browserStore(),
  });
}

export function currentFirstTouch(): FirstTouchAttribution | null {
  if (typeof window === 'undefined') return null;
  return readFirstTouch(browserStore());
}
